/**
 * IPAMVirtualGroups — /networks/vips (VIPs / CARP)
 *
 * Un **grupo virtual** (VIP / CARP / VRRP / HSRP / Keepalived) es un clúster de
 * CIs que comparten una misma IP virtual: la que responde aunque uno de los
 * nodos caiga. Cada miembro aporta su rol (master/backup/member) y su prioridad
 * (mayor gana, convención CARP/VRRP) y puede llevar enlazada su IP física real.
 *
 * Antes esta pantalla era maestro-detalle (panel de grupos + detalle siempre
 * visible) con estilos oscuros en línea, sin buscador, sin filtros, sin orden y
 * sin export. Ahora:
 *  · `ListPage` lista los grupos (buscador, filtros, columnas, vistas, export y
 *    botones `[acciones propias] · Copiar · Editar · Eliminar`).
 *  · Al pulsar una fila se abre la **ficha del grupo en lectura**, con su IP
 *    virtual y su tabla de miembros dentro (esa tabla conserva la edición en
 *    línea y el alta de miembros, que es donde se gestiona el clúster).
 *
 * El site no existe en el modelo del grupo: se deduce de la **subred de la IP
 * virtual** (`subnet_cidr` → site de esa subred), igual que «tiene DHCP» en
 * Subredes. Así el filtro y la columna de site son datos reales, no inventados.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  PlusIcon,
  PencilSquareIcon,
  TrashIcon,
  ShieldCheckIcon,
  XMarkIcon,
  CheckIcon,
  ServerStackIcon,
  ListBulletIcon,
  Squares2X2Icon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import IPAMLayout from './IPAMLayout';
import FreeIpPicker from './FreeIpPicker';
import ListPage from './common/ListPage';
import Dialog from './common/Dialog';
import api from '../services/api';
import { compareText } from '../utils/ipamSort';
import { useListView } from '../views/ViewsContext';

/* ── constants ───────────────────────────────────────────────────────────── */

const PROTOCOL_LABELS = {
  carp:        'CARP',
  vrrp:        'VRRP',
  hsrp:        'HSRP',
  vip:         'VIP',
  virtual:     'Virtual',
  keepalived:  'Keepalived',
};

/* Clases de badge del estándar de listados (nada de `bg-*` de Bootstrap). */
const PROTOCOL_BADGE = {
  carp:       'list-badge--on',
  vrrp:       'list-badge--on',
  hsrp:       'list-badge--warn',
  vip:        'list-badge',
  virtual:    'list-badge--warn',
  keepalived: 'list-badge',
};

const STATUS_META = {
  active:      { label: 'Activo',        badge: 'list-badge--on' },
  inactive:    { label: 'Inactivo',      badge: 'list-badge--off' },
  maintenance: { label: 'Mantenimiento', badge: 'list-badge--warn' },
};

const OBJECT_TYPE_LABELS = {
  server:           'Servidor',
  server_team:      'Team NIC',
  rack_element:     'Elem. rack',
  workstation:      'Workstation',
  printer:          'Impresora',
  ip_phone:         'Teléfono IP',
  firewall:         'Firewall',
  router:           'Router',
  virt_vm:          'Máquina virtual',
};

/* Tipos de CI que pueden formar parte de un grupo VIP/CARP. Los tres primeros
   son los casos típicos de IP compartida (HA de firewalls/routers); el backend
   acepta cualquier `object_type`, así que ampliar esta lista basta. */
const MEMBER_TYPE_OPTIONS = [
  { value: 'firewall',     label: 'Firewall' },
  { value: 'router',       label: 'Router' },
  { value: 'server',       label: 'Servidor' },
  { value: 'rack_element', label: 'Elemento de rack (switch L3…)' },
  { value: 'virt_vm',      label: 'Máquina virtual' },
  { value: 'workstation',  label: 'Workstation' },
];

function memberLabel(type, item) {
  if (!item) return '#';
  switch (type) {
    case 'rack_element':
      return `[${item.element_type || 'elem'}] ${item.name || item.code || `#${item.id}`}`;
    case 'workstation':
      return `${item.code || `WS #${item.id}`}${item.name ? ` · ${item.name}` : ''}`;
    default:
      return item.name || item.hostname || item.code || `#${item.id}`;
  }
}

const EMPTY_CATALOGS = {
  server: [], firewall: [], router: [], rack_element: [], virt_vm: [], workstation: [],
};

const EMPTY_GROUP_FORM = {
  name: '', protocol: 'carp', ip: '', description: '', status: 'active',
};

const EMPTY_MEMBER_FORM = {
  object_type: 'server', object_id: '', priority: 100, role: 'backup',
  physical_assignment_id: '', physical_ip: '',
};

/* ── helpers ─────────────────────────────────────────────────────────────── */

function protocolBadge(protocol) {
  const label = PROTOCOL_LABELS[protocol] || protocol;
  return (
    <span className={`list-badge ${PROTOCOL_BADGE[protocol] || ''}`.trim()}>
      {String(label).toUpperCase()}
    </span>
  );
}

function roleBadge(role) {
  const cls = role === 'master' ? 'list-badge--on' : role === 'backup' ? 'list-badge--warn' : '';
  return <span className={`list-badge ${cls}`.trim()}>{role}</span>;
}

function statusBadge(status) {
  const meta = STATUS_META[status] || STATUS_META.active;
  return <span className={`list-badge ${meta.badge}`}>{meta.label}</span>;
}

/* ─────────────────────────────────────────────────────────────────────────── */

export default function IPAMVirtualGroups({ companyId }) {
  const { t } = useTranslation();
  const tr = useCallback((key, fallback) => {
    const value = t(key);
    return !value || value === key ? fallback : value;
  }, [t]);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState('');
  const [success, setSuccess]     = useState('');
  const errTimer                  = useRef(null);
  const [saving, setSaving]       = useState(false);

  const [groups, setGroups]       = useState([]);

  /* catálogos por tipo: resuelven nombres y pueblan el selector de miembros */
  const [catalogs, setCatalogs]   = useState(EMPTY_CATALOGS);

  /* subredes: dan el site que el grupo no guarda (se deduce de su CIDR) */
  const [subnets, setSubnets]     = useState([]);

  /* filtros */
  const [search, setSearch]             = useState('');
  const [filterProtocol, setFilterProtocol] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterIp, setFilterIp]         = useState('');
  const [filterMembers, setFilterMembers] = useState('');
  const [filterMemberType, setFilterMemberType] = useState('');
  const [filterSite, setFilterSite]     = useState('');
  const [hlId, setHlId]                 = useState(null);
  const [knownSites, setKnownSites]     = useState([]);

  /* ficha del grupo (lectura) */
  const [detailId, setDetailId]         = useState(null);
  const [focusMembers, setFocusMembers] = useState(false);

  /* group form (create/edit) */
  const [groupModal, setGroupModal]   = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);   // null = create
  const [groupForm, setGroupForm]     = useState(EMPTY_GROUP_FORM);

  /* member form */
  const [memberForm, setMemberForm]   = useState(EMPTY_MEMBER_FORM);
  const [addingMember, setAddingMember] = useState(false);
  const [memberSearch, setMemberSearch] = useState('');

  /* inline member edit */
  const [editMemberId, setEditMemberId] = useState(null);
  const [editMemberForm, setEditMemberForm] = useState({});

  /* delete confirmation: { kind: 'group'|'member', group, member? } */
  const [confirmDelete, setConfirmDelete] = useState(null);

  /* ── avisos con autocierre ── */
  useEffect(() => {
    if (error) {
      clearTimeout(errTimer.current);
      errTimer.current = setTimeout(() => setError(''), 7000);
    }
    return () => clearTimeout(errTimer.current);
  }, [error]);

  useEffect(() => {
    if (!success) return undefined;
    const timer = setTimeout(() => setSuccess(''), 9000);
    return () => clearTimeout(timer);
  }, [success]);

  /* ── data loading ─────────────────────────────────────────────── */
  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const [gRes, sRes, fRes, rRes, eRes, vRes, wRes, subRes] = await Promise.allSettled([
        api.getIpamVirtualGroups(companyId),
        api.getServers(companyId),
        api.getFirewalls(companyId),
        api.getRouters(companyId),
        api.getElements(companyId),
        api.getVMs(companyId),
        api.getWorkstations({ company_id: companyId }),
        api.getIpamSubnets(companyId),
      ]);
      if (gRes.status === 'fulfilled') setGroups(gRes.value.data || []);
      else setError(gRes.reason?.message || 'Error cargando grupos');
      const dataOf = (r) => (r.status === 'fulfilled' ? (r.value?.data || []) : []);
      setCatalogs({
        server:       dataOf(sRes),
        firewall:     dataOf(fRes),
        router:       dataOf(rRes),
        rack_element: dataOf(eRes),
        virt_vm:      dataOf(vRes),
        workstation:  dataOf(wRes),
      });
      setSubnets(dataOf(subRes));
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  /* ── site deducido de la subred de la IP virtual ───────────────── */
  const siteByCidr = useMemo(() => {
    const map = {};
    subnets.forEach(s => { if (s.cidr) map[s.cidr] = s.site || ''; });
    return map;
  }, [subnets]);

  const siteOf = useCallback(
    (group) => (group?.subnet_cidr ? (siteByCidr[group.subnet_cidr] || '') : ''),
    [siteByCidr],
  );

  useEffect(() => {
    const sites = new Set();
    groups.forEach(g => { const s = siteOf(g); if (s) sites.add(s); });
    if (sites.size) {
      setKnownSites(prev => [...new Set([...prev, ...sites])].sort((a, b) => compareText(a, b)));
    }
  }, [groups, siteOf]);

  /* ── entrada directa desde otra pantalla (?destacar / ?abrir) ──── */
  useEffect(() => {
    const raw = searchParams.get('destacar');
    if (!raw || !groups.length) return undefined;
    const id = Number(raw);
    const target = groups.find(g => g.id === id);
    if (!target) return undefined;
    const abrir = searchParams.get('abrir') === '1';
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('destacar');
      next.delete('abrir');
      return next;
    }, { replace: true });
    if (abrir) { setDetailId(id); return undefined; }
    setHlId(id);
    const row = document.getElementById(`vip-row-${id}`);
    if (row) row.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const timer = setTimeout(() => setHlId(null), 4000);
    return () => clearTimeout(timer);
  }, [searchParams, groups, setSearchParams]);

  /* ── derived ───────────────────────────────────────────────────── */
  const detailGroup = groups.find(g => g.id === detailId) || null;

  /* object name resolver */
  const objectName = useCallback((type, id) => {
    const row = (catalogs[type] || []).find(x => x.id === id);
    if (row) return memberLabel(type, row);
    return `${OBJECT_TYPE_LABELS[type] || type} #${id}`;
  }, [catalogs]);

  /* opciones de miembro del tipo elegido, filtradas por búsqueda */
  const memberOptionsFor = useCallback((type, q) => {
    const rows = catalogs[type] || [];
    const needle = (q || '').trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter(r => [r.name, r.hostname, r.code, r.element_type, r.primary_ip, r.ip_address]
      .filter(Boolean).join(' ').toLowerCase().includes(needle));
  }, [catalogs]);

  /* ── abrir la ficha ────────────────────────────────────────────── */
  const openDetail = useCallback((group, { members = false } = {}) => {
    setDetailId(group.id);
    setFocusMembers(members);
  }, []);

  const closeDetail = () => {
    setDetailId(null);
    setFocusMembers(false);
    setEditMemberId(null);
    setAddingMember(false);
  };

  /* ── group CRUD ────────────────────────────────────────────────── */
  const openCreateGroup = () => {
    setEditingGroup(null);
    setGroupForm(EMPTY_GROUP_FORM);
    setGroupModal(true);
  };

  const openEditGroup = (group) => {
    setEditingGroup(group);
    setGroupForm({
      name: group.name,
      protocol: group.protocol,
      ip: group.ip || '',
      description: group.description || '',
      status: group.status,
    });
    setGroupModal(true);
  };

  /** Copiar grupo: se precarga todo menos la IP virtual (es única). */
  const openCopyGroup = (group) => {
    if (!group) return;
    setEditingGroup(null);
    setGroupForm({
      ...EMPTY_GROUP_FORM,
      name: group.name ? `${group.name} (copia)` : '',
      protocol: group.protocol,
      description: group.description || '',
      status: group.status,
    });
    setGroupModal(true);
  };

  const saveGroup = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingGroup) {
        await api.updateIpamVirtualGroup(editingGroup.id, groupForm, companyId);
        setSuccess(`Grupo «${groupForm.name}» actualizado.`);
      } else {
        await api.createIpamVirtualGroup(groupForm, companyId);
        setSuccess(`Grupo «${groupForm.name}» creado.`);
      }
      setGroupModal(false);
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const deleteGroup = async (group) => {
    setConfirmDelete(null);
    try {
      await api.deleteIpamVirtualGroup(group.id, companyId);
      if (detailId === group.id) closeDetail();
      setSuccess(`Grupo «${group.name}» eliminado y sus IP virtuales liberadas.`);
      await load();
    } catch (err) {
      setError(err.message);
    }
  };

  /* ── member CRUD ───────────────────────────────────────────────── */
  const addMember = async (e) => {
    e.preventDefault();
    if (!detailGroup) return;
    setSaving(true);
    try {
      await api.addIpamVirtualGroupMember(detailGroup.id, {
        ...memberForm,
        object_id: Number(memberForm.object_id),
        priority: Number(memberForm.priority),
        physical_assignment_id: memberForm.physical_assignment_id ? Number(memberForm.physical_assignment_id) : null,
        physical_ip: memberForm.physical_ip || null,
      }, companyId);
      setMemberForm(EMPTY_MEMBER_FORM);
      setMemberSearch('');
      setAddingMember(false);
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const startEditMember = (m) => {
    setEditMemberId(m.id);
    setEditMemberForm({
      priority: m.priority,
      role: m.role,
      physical_assignment_id: m.physical_assignment_id != null ? String(m.physical_assignment_id) : '',
    });
  };

  const saveEditMember = async (groupId, memberId) => {
    setSaving(true);
    try {
      await api.updateIpamVirtualGroupMember(groupId, memberId, {
        priority: Number(editMemberForm.priority),
        role: editMemberForm.role,
        physical_assignment_id: editMemberForm.physical_assignment_id ? Number(editMemberForm.physical_assignment_id) : null,
      }, companyId);
      setEditMemberId(null);
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const removeMember = async (groupId, memberId) => {
    setConfirmDelete(null);
    try {
      await api.removeIpamVirtualGroupMember(groupId, memberId, companyId);
      await load();
    } catch (err) {
      setError(err.message);
    }
  };

  /* ── filtrado ──────────────────────────────────────────────────── */
  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return groups.filter((g) => {
      if (filterProtocol && g.protocol !== filterProtocol) return false;
      if (filterStatus && (g.status || 'active') !== filterStatus) return false;
      if (filterIp === 'with' && !g.ip) return false;
      if (filterIp === 'without' && g.ip) return false;
      const members = g.members || [];
      if (filterMembers === 'with' && members.length === 0) return false;
      if (filterMembers === 'without' && members.length > 0) return false;
      if (filterMemberType && !members.some(m => m.object_type === filterMemberType)) return false;
      if (filterSite && siteOf(g) !== filterSite) return false;
      if (!needle) return true;
      return (g.name || '').toLowerCase().includes(needle)
        || (g.ip || '').toLowerCase().includes(needle)
        || (g.subnet_cidr || '').toLowerCase().includes(needle)
        || (g.description || '').toLowerCase().includes(needle)
        || siteOf(g).toLowerCase().includes(needle);
    });
  }, [groups, search, filterProtocol, filterStatus, filterIp, filterMembers, filterMemberType, filterSite, siteOf]);

  /* ── columnas (8 de catálogo; 7 visibles de entrada) ───────────── */
  const columns = useMemo(() => {
    const dash = <span className="list-hint">—</span>;
    return [
      {
        key: 'name',
        label: tr('vip.name', 'Grupo'),
        sortable: true,
        render: (g) => <span style={{ fontWeight: 600 }}>{g.name || dash}</span>,
      },
      {
        key: 'ip',
        label: tr('vip.ip', 'IP virtual'),
        sortable: true,
        sortValue: (g) => g.ip || '',
        render: (g) => (g.ip
          ? <span className="list-code" style={{ fontWeight: 700 }}>{g.ip}</span>
          : <span className="list-hint">{tr('vip.no_ip', 'Sin asignar')}</span>),
      },
      {
        key: 'subnet_cidr',
        label: tr('vip.subnet', 'Subred'),
        sortable: true,
        sortValue: (g) => g.subnet_cidr || '',
        render: (g) => (g.subnet_cidr
          ? (
            <button
              type="button"
              className="list-code"
              style={{ cursor: 'pointer', background: 'none', border: 0, padding: 0 }}
              title={tr('vip.view_subnet', 'Ver la subred de la IP virtual')}
              onClick={(event) => { event.stopPropagation(); navigate(`/networks/subnets?cidr=${encodeURIComponent(g.subnet_cidr)}`); }}
            >
              {g.subnet_cidr}
            </button>
          )
          : dash),
      },
      {
        key: 'site',
        label: tr('ipam.site', 'Site'),
        sortable: true,
        sortValue: (g) => siteOf(g),
        render: (g) => siteOf(g) || dash,
      },
      {
        key: 'protocol',
        label: tr('vip.protocol', 'Protocolo'),
        sortable: true,
        render: (g) => protocolBadge(g.protocol),
      },
      {
        key: 'status',
        label: tr('ipam.status', 'Estado'),
        sortable: true,
        sortValue: (g) => g.status || 'active',
        render: (g) => statusBadge(g.status || 'active'),
      },
      {
        key: 'member_count',
        label: tr('vip.members', 'Miembros'),
        sortable: true,
        sortValue: (g) => g.member_count ?? (g.members || []).length,
        render: (g) => {
          const count = (g.members || []).length;
          return count
            ? (
              <button
                type="button"
                className="list-badge list-badge--on"
                style={{ cursor: 'pointer' }}
                title={tr('vip.view_members', 'Ver los miembros del grupo')}
                onClick={(event) => { event.stopPropagation(); openDetail(g, { members: true }); }}
              >
                {count}
              </button>
            )
            : <span className="list-badge list-badge--off">{tr('vip.no_members', 'Sin miembros')}</span>;
        },
      },
      {
        key: 'description',
        label: tr('description', 'Descripción'),
        sortable: true,
        render: (g) => g.description || dash,
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, tr, navigate, siteOf, openDetail]);

  const defaultVisible = ['name', 'ip', 'subnet_cidr', 'site', 'protocol', 'status', 'member_count'];

  const filters = [
    {
      key: 'protocol', label: tr('vip.protocol', 'Protocolo'), allLabel: tr('vip.protocol_all', 'Todos los protocolos'),
      value: filterProtocol, onChange: setFilterProtocol,
      options: Object.entries(PROTOCOL_LABELS).map(([value, label]) => ({ value, label })),
    },
    {
      key: 'status', label: tr('ipam.status', 'Estado'), allLabel: tr('ipam.status_all', 'Todos los estados'),
      value: filterStatus, onChange: setFilterStatus,
      options: Object.entries(STATUS_META).map(([value, meta]) => ({ value, label: meta.label })),
    },
    {
      key: 'ip', label: tr('vip.ip', 'IP virtual'), allLabel: tr('vip.ip_all', 'Con y sin IP virtual'),
      value: filterIp, onChange: setFilterIp,
      options: [
        { value: 'with', label: tr('vip.ip_with', 'Con IP asignada') },
        { value: 'without', label: tr('vip.ip_without', 'Sin IP asignada') },
      ],
    },
    {
      key: 'members', label: tr('vip.members', 'Miembros'), allLabel: tr('vip.members_all', 'Con y sin miembros'),
      value: filterMembers, onChange: setFilterMembers,
      options: [
        { value: 'with', label: tr('vip.members_with', 'Con miembros') },
        { value: 'without', label: tr('vip.members_without', 'Sin miembros') },
      ],
    },
    {
      key: 'memberType', label: tr('vip.member_type', 'Tipo de miembro'), allLabel: tr('vip.member_type_all', 'Cualquier tipo'),
      value: filterMemberType, onChange: setFilterMemberType,
      options: MEMBER_TYPE_OPTIONS.map(o => ({ value: o.value, label: o.label })),
    },
    {
      key: 'site', label: tr('ipam.site', 'Site'), allLabel: tr('ipam.site_all', 'Todos los sites'),
      value: filterSite, onChange: setFilterSite,
      options: knownSites.map(s => ({ value: s, label: s })),
    },
  ];

  const subtitle = `${groups.length} ${tr('vip.plural', 'grupos')}`
    + ` · ${filtered.length} ${tr('location.filtered', 'filtrados')}`;

  const exportRows = useMemo(() => filtered.map(g => ({
    'Grupo': g.name || '',
    'IP virtual': g.ip || '',
    'Subred': g.subnet_cidr || '',
    'Site': siteOf(g),
    'Protocolo': PROTOCOL_LABELS[g.protocol] || g.protocol,
    'Estado': (STATUS_META[g.status || 'active'] || STATUS_META.active).label,
    'Miembros': (g.members || []).length,
    'Descripción': g.description || '',
  })), [filtered, siteOf]);

  /** Tarjetas: lo esencial del grupo, con los mismos botones de fila. */
  function renderCards({ rows, rowActions }) {
    return (
      <div className="list-cards">
        {rows.map((g) => (
          <div
            key={g.id}
            id={`vip-card-${g.id}`}
            className={`list-card${g.id === hlId ? ' list-row-highlight' : ''}`}
            role="button"
            tabIndex={0}
            onClick={() => openDetail(g)}
            onKeyDown={(event) => { if (event.key === 'Enter') openDetail(g); }}
            title={`${tr('action.view', 'Ver')} ${g.name}`}
          >
            <div className="list-card-head">
              <div>
                <div className="list-card-title">{g.name}</div>
                {g.ip
                  ? <span className="list-code">{g.ip}</span>
                  : <span className="list-hint">{tr('vip.no_ip', 'Sin asignar')}</span>}
              </div>
              {protocolBadge(g.protocol)}
            </div>

            <div className="list-card-meta">
              <span className={`list-badge ${(STATUS_META[g.status || 'active'] || STATUS_META.active).badge}`}>
                {(STATUS_META[g.status || 'active'] || STATUS_META.active).label}
              </span>
              {g.subnet_cidr && <span className="list-code">{g.subnet_cidr}</span>}
              {siteOf(g) && <span>📍 {siteOf(g)}</span>}
              <span>
                {(g.members || []).length
                  ? `${(g.members || []).length} ${tr('vip.members_lower', 'miembros')}`
                  : tr('vip.no_members', 'Sin miembros')}
              </span>
            </div>

            {g.description && <div className="list-card-meta"><span>{g.description}</span></div>}

            <div className="list-card-actions">{rowActions(g)}</div>
          </div>
        ))}
      </div>
    );
  }

  const VIP_VIEWS = useMemo(() => [
    { key: 'list', label: tr('ipam.view_list', 'Lista'), icon: ListBulletIcon, title: tr('ipam.view_list_hint', 'Tabla con todas las columnas') },
    { key: 'card', label: tr('ipam.view_cards', 'Fichas'), icon: Squares2X2Icon, title: tr('ipam.view_cards_hint', 'Tarjetas con lo esencial de cada grupo'), render: renderCards },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [t, hlId, siteOf]);
  useListView('ipam_vips', 'list');

  if (!companyId) {
    return (
      <IPAMLayout>
        <div className="alert alert-warning">{tr('ipam.select_company', 'Selecciona una empresa para ver el IPAM.')}</div>
      </IPAMLayout>
    );
  }

  /* ── render ────────────────────────────────────────────────────── */
  return (
    <IPAMLayout>
      {/* Avisos */}
      {error && (
        <div className="alert alert-danger py-2 d-flex justify-content-between align-items-center mb-3">
          <span>{error}</span>
          <button className="btn-close" onClick={() => setError('')} />
        </div>
      )}
      {success && (
        <div className="alert alert-success py-2 d-flex justify-content-between align-items-center mb-3">
          <span>{success}</span>
          <button className="btn-close" onClick={() => setSuccess('')} />
        </div>
      )}

      <ListPage
        icon={ShieldCheckIcon}
        title={tr('vip.title', 'VIPs / CARP')}
        subtitle={subtitle}

        onCreate={openCreateGroup}
        createLabel={tr('vip.new', 'Nuevo grupo')}
        createTitle={tr('vip.new_hint', 'Crear un grupo de IP compartida (CARP / VRRP / HSRP)')}

        search={search}
        onSearchChange={setSearch}
        searchPlaceholder={tr('vip.search', 'Buscar grupo, IP virtual, subred…')}
        filters={filters}

        exportRows={exportRows}
        exportFileBase="vips"
        exportSheetName="VIPs"

        columns={columns}
        columnStorageKey="cmdb_cols_ipam_vips"
        defaultVisible={defaultVisible}
        rows={filtered}
        rowKey={(g) => g.id}
        rowId={(g) => `vip-row-${g.id}`}
        rowClassName={(g) => (g.id === hlId ? 'list-row-highlight' : '')}
        onRowClick={openDetail}
        rowTitle={(g) => `${tr('action.view', 'Ver')} ${g.name}`}

        views={VIP_VIEWS}
        listKey="ipam_vips"
        defaultView="list"

        loading={loading}
        loadingText={tr('msg.loading', 'Cargando grupos VIP…')}

        empty={{
          icon: '🛡️',
          title: groups.length === 0
            ? tr('vip.empty', 'No hay grupos virtuales')
            : tr('msg.no_results', 'Ningún grupo coincide con los filtros'),
          subtitle: groups.length === 0
            ? tr('vip.empty_hint', 'Crea uno para agrupar los CIs que comparten una IP (en HA, los dos nodos).')
            : undefined,
          action: groups.length === 0 ? (
            <button type="button" className="list-btn-primary" onClick={openCreateGroup}>
              ＋ {tr('vip.new', 'Nuevo grupo')}
            </button>
          ) : undefined,
        }}

        actions={{
          extra: (g) => (
            <>
              <button
                type="button"
                className="list-action-btn"
                onClick={() => openDetail(g, { members: true })}
                title={tr('vip.view_members', 'Ver los miembros del grupo')}
              >
                <ServerStackIcon />
              </button>
              <button
                type="button"
                className="list-action-btn"
                disabled={!g.subnet_cidr}
                onClick={() => navigate(`/networks/subnets?cidr=${encodeURIComponent(g.subnet_cidr)}`)}
                title={tr('vip.view_subnet', 'Ver la subred de la IP virtual')}
              >
                <MagnifyingGlassIcon />
              </button>
            </>
          ),
          onCopy: openCopyGroup,
          onEdit: openEditGroup,
          onDelete: (g) => setConfirmDelete({ kind: 'group', group: g }),
          deleteTitle: tr('vip.delete_title', 'Eliminar grupo'),
          deleteMessage: (g) => (
            <>
              {tr('vip.delete_confirm', 'Se eliminará el grupo')} <strong>{g.name}</strong>{' '}
              {tr('vip.delete_members', 'con sus')} {(g.members || []).length} {tr('vip.members_lower', 'miembros')}.{' '}
              {tr('vip.delete_hint', 'Las asignaciones de IP virtual de cada miembro serán liberadas. No se puede deshacer.')}
            </>
          ),
        }}
      />

      {/* ── Alta / edición del grupo ──────────────────────────────────── */}
      {groupModal && (
        <GroupModal
          form={groupForm}
          onChange={setGroupForm}
          editing={editingGroup}
          onSubmit={saveGroup}
          onClose={() => setGroupModal(false)}
          saving={saving}
          companyId={companyId}
        />
      )}

      {/* ── Ficha del grupo (lectura) con su tabla de miembros ─────────── */}
      {detailGroup && (
        <VirtualGroupDetail
          group={detailGroup}
          site={siteOf(detailGroup)}
          companyId={companyId}
          catalogs={catalogs}
          objectName={objectName}
          memberOptionsFor={memberOptionsFor}
          saving={saving}
          focusMembers={focusMembers}
          onClose={closeDetail}
          onEdit={() => openEditGroup(detailGroup)}
          onDelete={() => setConfirmDelete({ kind: 'group', group: detailGroup })}
          addingMember={addingMember}
          memberForm={memberForm}
          onMemberFormChange={setMemberForm}
          memberSearch={memberSearch}
          onMemberSearchChange={setMemberSearch}
          onStartAddMember={() => { setAddingMember(true); setMemberForm(EMPTY_MEMBER_FORM); setMemberSearch(''); }}
          onCancelAddMember={() => setAddingMember(false)}
          onSubmitMember={addMember}
          editMemberId={editMemberId}
          editMemberForm={editMemberForm}
          onEditMemberFormChange={setEditMemberForm}
          onStartEditMember={startEditMember}
          onSaveEditMember={(memberId) => saveEditMember(detailGroup.id, memberId)}
          onCancelEditMember={() => setEditMemberId(null)}
          onRemoveMember={(member) => setConfirmDelete({ kind: 'member', group: detailGroup, member })}
        />
      )}

      {/* ── Confirmación (mismo diálogo del estándar) ──────────────────── */}
      {confirmDelete && (
        <Dialog
          title={confirmDelete.kind === 'group'
            ? tr('vip.delete_title', 'Eliminar grupo')
            : tr('vip.remove_member_title', 'Quitar miembro')}
          icon={<TrashIcon style={{ width: 18, height: 18 }} />}
          onClose={() => setConfirmDelete(null)}
          footer={(
            <>
              <button type="button" className="list-btn-secondary" onClick={() => setConfirmDelete(null)}>
                {tr('action.cancel', 'Cancelar')}
              </button>
              <button
                type="button"
                className="list-btn-danger"
                onClick={() => (confirmDelete.kind === 'group'
                  ? deleteGroup(confirmDelete.group)
                  : removeMember(confirmDelete.group.id, confirmDelete.member.id))}
              >
                {tr('action.confirm', 'Confirmar')}
              </button>
            </>
          )}
        >
          <p style={{ margin: 0 }}>
            {confirmDelete.kind === 'group' ? (
              <>
                {tr('vip.delete_confirm', 'Se eliminará el grupo')} <strong>{confirmDelete.group.name}</strong>{' '}
                {tr('vip.delete_members', 'con sus')} {(confirmDelete.group.members || []).length}{' '}
                {tr('vip.members_lower', 'miembros')}.{' '}
                {tr('vip.delete_hint', 'Las asignaciones de IP virtual de cada miembro serán liberadas. No se puede deshacer.')}
              </>
            ) : (
              <>
                <strong>{objectName(confirmDelete.member.object_type, confirmDelete.member.object_id)}</strong>{' '}
                {tr('vip.remove_member_text', 'saldrá del grupo y su asignación de IP virtual será liberada.')}
              </>
            )}
          </p>
        </Dialog>
      )}
    </IPAMLayout>
  );
}

/* ── Ficha del grupo (lectura) + gestión de miembros ─────────────────────────
   Se abre al pulsar una fila. Los datos del grupo se ven en lectura (para
   cambiarlos, «Editar» abre `GroupModal`); la tabla de miembros sí se gestiona
   aquí mismo, que es donde tiene sentido: alta, edición en línea y quitar.
────────────────────────────────────────────────────────────────────────────── */
function VirtualGroupDetail({
  group, site, companyId, catalogs, objectName, memberOptionsFor, saving, focusMembers,
  onClose, onEdit, onDelete,
  addingMember, memberForm, onMemberFormChange, memberSearch, onMemberSearchChange,
  onStartAddMember, onCancelAddMember, onSubmitMember,
  editMemberId, editMemberForm, onEditMemberFormChange,
  onStartEditMember, onSaveEditMember, onCancelEditMember, onRemoveMember,
}) {
  const { t } = useTranslation();
  const tr = (key, fallback) => {
    const value = t(key);
    return !value || value === key ? fallback : value;
  };
  const membersEl = useRef(null);

  useEffect(() => {
    if (focusMembers && membersEl.current) {
      membersEl.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [focusMembers]);

  const members = (group.members || []).slice().sort((a, b) => b.priority - a.priority);

  return (
    <div
      className="modal d-block"
      tabIndex="-1"
      style={{ background: 'rgba(0,0,0,0.45)', zIndex: 1060 }}
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <div className="modal-dialog modal-xl modal-dialog-scrollable">
        <div className="modal-content shadow-lg">

          <div className="modal-header py-3">
            <div>
              <div className="d-flex align-items-center gap-2 mb-1">
                {protocolBadge(group.protocol)}
                {statusBadge(group.status)}
              </div>
              <h5 className="modal-title fw-bold">{group.name}</h5>
              {group.description && <span className="list-hint">{group.description}</span>}
            </div>
            <div className="d-flex gap-2 ms-auto me-2">
              <button type="button" className="list-btn-secondary" onClick={onEdit} disabled={saving}>
                <PencilSquareIcon style={{ width: 15, height: 15 }} /> Editar
              </button>
              <button type="button" className="list-btn-secondary" onClick={onDelete} disabled={saving}>
                <TrashIcon style={{ width: 15, height: 15 }} /> Eliminar
              </button>
            </div>
            <button className="btn-close" onClick={onClose} />
          </div>

          <div className="modal-body">
            {/* ── Datos en lectura ──────────────────────────────────── */}
            <div className="list-kpi-group" style={{ gap: '1.5rem', marginBottom: '1rem' }}>
              <div>
                <div className="list-hint">{tr('vip.ip_label', 'IP Virtual')} ({PROTOCOL_LABELS[group.protocol] || group.protocol})</div>
                <div className="list-code" style={{ fontSize: '1.15em', fontWeight: 700 }}>
                  {group.ip || <span className="list-hint">{tr('vip.no_ip', 'Sin asignar')}</span>}
                </div>
              </div>
              <div>
                <div className="list-hint">{tr('vip.subnet', 'Subred')}</div>
                <div className="list-code">{group.subnet_cidr || '—'}</div>
              </div>
              <div>
                <div className="list-hint">{tr('ipam.site', 'Site')}</div>
                <div>{site || '—'}</div>
              </div>
              <div>
                <div className="list-hint">{tr('vip.members', 'Miembros')}</div>
                <div>{members.length}</div>
              </div>
            </div>

            {/* ── Miembros ──────────────────────────────────────────── */}
            <div ref={membersEl} className="d-flex align-items-center justify-content-between mb-3">
              <h6 className="mb-0 fw-semibold">
                {tr('vip.members', 'Miembros')}
                <span className="list-badge ms-2">{members.length}</span>
              </h6>
              {!addingMember && (
                <button type="button" className="list-btn-primary" onClick={onStartAddMember}>
                  <PlusIcon style={{ width: 14, height: 14 }} /> {tr('vip.add_member', 'Añadir miembro')}
                </button>
              )}
            </div>

            {members.length === 0 && !addingMember ? (
              <div className="list-empty" style={{ padding: '1.5rem' }}>
                <ServerStackIcon style={{ width: 36, height: 36, opacity: 0.3 }} />
                <p className="list-hint" style={{ marginTop: '0.5rem' }}>
                  {tr('vip.members_empty', 'Sin miembros: añade los CIs que comparten la IP (en HA, los dos nodos).')}
                </p>
              </div>
            ) : (
              <div className="table-responsive mb-3">
                <table className="table table-sm table-hover align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: '30%' }}>{tr('vip.col_object', 'Objeto')}</th>
                      <th style={{ width: '12%' }}>{tr('vip.col_role', 'Rol')}</th>
                      <th style={{ width: '12%' }}>{tr('vip.col_priority', 'Prioridad')}</th>
                      <th style={{ width: '28%' }}>{tr('vip.col_physical', 'IP Física')}</th>
                      <th className="text-end" style={{ width: '18%' }}>{tr('list.actions', 'Acciones')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {members.map(m => (
                      <MemberRow
                        key={m.id}
                        member={m}
                        groupId={group.id}
                        objectName={objectName(m.object_type, m.object_id)}
                        isEditing={editMemberId === m.id}
                        editForm={editMemberForm}
                        onEditFormChange={onEditMemberFormChange}
                        onStartEdit={() => onStartEditMember(m)}
                        onSaveEdit={() => onSaveEditMember(m.id)}
                        onCancelEdit={onCancelEditMember}
                        onDelete={() => onRemoveMember(m)}
                        saving={saving}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {addingMember && (
              <AddMemberForm
                form={memberForm}
                onFormChange={onMemberFormChange}
                catalogs={catalogs}
                memberOptionsFor={memberOptionsFor}
                memberSearch={memberSearch}
                onSearchChange={onMemberSearchChange}
                onSubmit={onSubmitMember}
                onCancel={onCancelAddMember}
                saving={saving}
                companyId={companyId}
              />
            )}
          </div>

          <div className="modal-footer py-2">
            <button type="button" className="list-btn-secondary" onClick={onClose}>
              {tr('action.close', 'Cerrar')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Sub-components ────────────────────────────────────────────────────────── */

function MemberRow({ member, objectName, isEditing, editForm, onEditFormChange, onStartEdit,
                     onSaveEdit, onCancelEdit, onDelete, saving }) {
  if (isEditing) {
    return (
      <tr className="table-active">
        <td>
          <span className="fw-semibold">{objectName}</span>
          <span className="text-muted ms-2 small">({OBJECT_TYPE_LABELS[member.object_type] || member.object_type})</span>
        </td>
        <td>
          <select className="form-select form-select-sm" value={editForm.role}
            onChange={e => onEditFormChange(f => ({ ...f, role: e.target.value }))}>
            <option value="master">master</option>
            <option value="backup">backup</option>
            <option value="member">member</option>
          </select>
        </td>
        <td>
          <input type="number" className="form-control form-control-sm" min={1} max={255}
            value={editForm.priority}
            onChange={e => onEditFormChange(f => ({ ...f, priority: e.target.value }))} />
        </td>
        <td>
          <input type="number" className="form-control form-control-sm" placeholder="ID asignación"
            style={{ maxWidth: 130 }}
            value={editForm.physical_assignment_id || ''}
            onChange={e => onEditFormChange(f => ({ ...f, physical_assignment_id: e.target.value }))} />
        </td>
        <td className="text-end">
          <button className="btn btn-sm btn-success me-1" onClick={onSaveEdit} disabled={saving}>
            <CheckIcon style={{ width: 13, height: 13 }} />
          </button>
          <button className="btn btn-sm btn-outline-secondary" onClick={onCancelEdit}>
            <XMarkIcon style={{ width: 13, height: 13 }} />
          </button>
        </td>
      </tr>
    );
  }

  return (
    <tr>
      <td>
        <div className="d-flex align-items-center gap-2">
          <div style={{ width: 28, height: 28, borderRadius: 6, background: 'rgba(99,102,241,.15)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <ServerStackIcon style={{ width: 14, height: 14, color: '#818cf8' }} />
          </div>
          <div>
            <div className="fw-semibold" style={{ fontSize: '0.88em' }}>{objectName}</div>
            <div className="text-muted" style={{ fontSize: '0.72em' }}>
              {OBJECT_TYPE_LABELS[member.object_type] || member.object_type}
            </div>
          </div>
        </div>
      </td>
      <td>{roleBadge(member.role)}</td>
      <td>
        <PriorityIndicator priority={member.priority} />
      </td>
      <td>
        {member.physical_ip
          ? (
            <div>
              <code style={{ fontSize: '0.85em', color: '#818cf8' }}>{member.physical_ip}</code>
              {member.interface_ref && (
                <span className="text-muted ms-1" style={{ fontSize: '0.75em' }}>({member.interface_ref})</span>
              )}
            </div>
          )
          : <span className="text-muted">—</span>}
      </td>
      <td className="text-end">
        <button className="btn btn-sm btn-outline-secondary me-1 py-0" style={{ fontSize: '0.78em' }}
          onClick={onStartEdit} title="Editar">
          <PencilSquareIcon style={{ width: 12, height: 12 }} />
        </button>
        <button className="btn btn-sm btn-outline-danger py-0" style={{ fontSize: '0.78em' }}
          onClick={onDelete} title="Quitar">
          <XMarkIcon style={{ width: 12, height: 12 }} />
        </button>
      </td>
    </tr>
  );
}

function PriorityIndicator({ priority }) {
  const prio = Number(priority);
  const color = prio >= 200 ? 'var(--green-600)' : prio >= 100 ? 'var(--amber-600)' : 'var(--text-muted)';
  return (
    <div className="d-flex align-items-center gap-1">
      <div style={{ width: 6, height: 6, borderRadius: '50%', background: color, flexShrink: 0 }} />
      <span style={{ fontFamily: 'monospace', fontSize: '0.85em' }}>{priority}</span>
    </div>
  );
}

function AddMemberForm({ form, onFormChange, catalogs, memberOptionsFor, memberSearch, onSearchChange, onSubmit, onCancel, saving, companyId }) {
  const [assignmentOpts, setAssignmentOpts] = useState([]);
  const [assignmentLoading, setAssignmentLoading] = useState(false);
  // 'existing' = elegir una IP que el objeto ya tiene en IPAM
  // 'new'      = darle una IP libre de una subred (no tiene ninguna todavía)
  const [ipSource, setIpSource] = useState('existing');

  useEffect(() => {
    if (!form.object_id || !companyId) {
      setAssignmentOpts([]);
      return;
    }
    setAssignmentLoading(true);
    // For servers, also load server_team assignments (bonded/teamed NICs) with the same object_id
    const baseQuery = api.getIpamAssignments(companyId, { object_type: form.object_type, object_id: form.object_id });
    const teamQuery = form.object_type === 'server'
      ? api.getIpamAssignments(companyId, { object_type: 'server_team', object_id: form.object_id })
      : Promise.resolve({ data: [] });
    Promise.all([baseQuery, teamQuery])
      .then(([baseRes, teamRes]) => {
        const baseRows = (baseRes?.data || []).filter(a => a.status === 'active')
          .map(a => ({ value: String(a.id), label: `${a.ip}${a.interface_ref ? ` · ${a.interface_ref}` : ''}` }));
        const teamRows = (teamRes?.data || []).filter(a => a.status === 'active')
          .map(a => ({ value: String(a.id), label: `${a.ip}${a.interface_ref ? ` · ${a.interface_ref}` : ''} (team)` }));
        const opts = [...baseRows, ...teamRows];
        setAssignmentOpts(opts);
        // Si el elemento todavía no tiene ninguna IP, lo que se puede hacer es
        // darle una libre: se abre directamente el selector de subred.
        setIpSource(opts.length === 0 ? 'new' : 'existing');
      })
      .catch(() => setAssignmentOpts([]))
      .finally(() => setAssignmentLoading(false));
  }, [form.object_type, form.object_id, companyId]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <form onSubmit={onSubmit}
      className="border rounded p-3 mt-2"
      style={{ background: 'rgba(99,102,241,.08)', border: '1px solid rgba(99,102,241,.2)' }}>
      <div className="fw-semibold small mb-3 text-primary">Añadir miembro al cluster</div>
      <div className="row g-2 align-items-end">

        {/* Object type */}
        <div className="col-auto">
          <label className="form-label small mb-1">Tipo</label>
          <select className="form-select form-select-sm" style={{ minWidth: 150 }}
            value={form.object_type}
              onChange={e => onFormChange(f => ({ ...f, object_type: e.target.value, object_id: '', physical_assignment_id: '', physical_ip: '' }))}>
            {MEMBER_TYPE_OPTIONS.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>

        {/* Object selector */}
        <div className="col" style={{ minWidth: 220 }}>
          <label className="form-label small mb-1">Objeto</label>
          {(catalogs[form.object_type] || []).length > 0 ? (
            <div>
              <input className="form-control form-control-sm mb-1" placeholder="Buscar…"
                value={memberSearch} onChange={e => onSearchChange(e.target.value)} />
              <select className="form-select form-select-sm" required
                value={form.object_id}
                onChange={e => onFormChange(f => ({ ...f, object_id: e.target.value, physical_assignment_id: '', physical_ip: '' }))}>
                <option value="">— Selecciona —</option>
                {memberOptionsFor(form.object_type, memberSearch).slice(0, 100).map(item => (
                  <option key={item.id} value={String(item.id)}>
                    {memberLabel(form.object_type, item)}
                    {item.primary_ip ? ` (${item.primary_ip})` : ''}
                  </option>
                ))}
              </select>
            </div>
          ) : (
            <input type="number" className="form-control form-control-sm" placeholder="ID del objeto" required
              value={form.object_id}
              onChange={e => onFormChange(f => ({ ...f, object_id: e.target.value, physical_assignment_id: '', physical_ip: '' }))} />
          )}
        </div>

        {/* Role */}
        <div className="col-auto">
          <label className="form-label small mb-1">Rol</label>
          <select className="form-select form-select-sm"
            value={form.role}
            onChange={e => onFormChange(f => ({ ...f, role: e.target.value }))}>
            <option value="master">master</option>
            <option value="backup">backup</option>
            <option value="member">member</option>
          </select>
        </div>

        {/* Priority */}
        <div className="col-auto">
          <label className="form-label small mb-1">Prioridad</label>
          <div className="d-flex align-items-center gap-2">
            <input type="range" className="form-range" min={1} max={255} step={1}
              style={{ width: 80 }}
              value={form.priority}
              onChange={e => onFormChange(f => ({ ...f, priority: Number(e.target.value) }))} />
            <span className="badge bg-secondary" style={{ minWidth: 32, fontFamily: 'monospace' }}>
              {form.priority}
            </span>
          </div>
        </div>

        {/* IP Física */}
        {form.object_id && (
          <div className="col-auto">
            <label className="form-label small mb-1">
              IP Física
              {assignmentLoading && <span className="spinner-border spinner-border-sm ms-1" style={{ width: 10, height: 10 }} />}
            </label>
            {ipSource === 'existing' ? (
              <select className="form-select form-select-sm" style={{ minWidth: 170 }}
                value={form.physical_assignment_id}
                onChange={e => onFormChange(f => ({ ...f, physical_assignment_id: e.target.value, physical_ip: '' }))}>
                <option value="">Sin enlazar</option>
                {assignmentOpts.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            ) : (
              <div style={{ minWidth: 230 }}>
                <FreeIpPicker companyId={companyId} value={form.physical_ip}
                  onChange={ip => onFormChange(f => ({ ...f, physical_ip: ip, physical_assignment_id: '' }))} />
              </div>
            )}
            <div className="form-text small mt-1" style={{ maxWidth: 260 }}>
              {ipSource === 'existing' ? (
                assignmentOpts.length === 0 ? (
                  <>
                    Este elemento no tiene IPs en IPAM.{' '}
                    <button type="button" className="btn btn-link btn-sm p-0 align-baseline"
                      onClick={() => setIpSource('new')}>
                      Dar una IP libre
                    </button>
                  </>
                ) : (
                  <button type="button" className="btn btn-link btn-sm p-0 align-baseline"
                    onClick={() => setIpSource('new')}>
                    Dar una IP nueva en su lugar
                  </button>
                )
              ) : (
                <>
                  Se creará la IP y se asignará al elemento.{' '}
                  <button type="button" className="btn btn-link btn-sm p-0 align-baseline"
                    onClick={() => { setIpSource('existing'); onFormChange(f => ({ ...f, physical_ip: '' })); }}>
                    {assignmentOpts.length ? 'Elegir una de las que ya tiene' : 'Volver'}
                  </button>
                </>
              )}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="col-auto d-flex gap-2">
          <button type="submit" className="btn btn-sm btn-primary" disabled={saving || !form.object_id}>
            {saving ? '…' : 'Añadir'}
          </button>
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onCancel}>
            Cancelar
          </button>
        </div>
      </div>
    </form>
  );
}

function GroupModal({ form, onChange, editing, onSubmit, onClose, saving, companyId }) {
  const [ipMode, setIpMode] = useState('manual'); // 'manual' | 'picker'

  return (
    <div className="modal show d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,0.4)' }}>
      <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 510 }}>
        <div className="modal-content">
          <form onSubmit={onSubmit}>
            <div className="modal-header py-3">
              <h6 className="modal-title fw-bold mb-0">
                {editing ? `Editar grupo: ${editing.name}` : 'Nuevo grupo virtual'}
              </h6>
              <button type="button" className="btn-close" onClick={onClose} />
            </div>
            <div className="modal-body">
              <div className="mb-3">
                <label className="form-label small fw-semibold">Nombre *</label>
                <input className="form-control" required placeholder="ej. cluster-web-prod"
                  value={form.name}
                  onChange={e => onChange(f => ({ ...f, name: e.target.value }))} />
              </div>

              <div className="row g-3 mb-3">
                <div className="col">
                  <label className="form-label small fw-semibold">Protocolo *</label>
                  <select className="form-select" required value={form.protocol}
                    onChange={e => onChange(f => ({ ...f, protocol: e.target.value }))}>
                    {Object.entries(PROTOCOL_LABELS).map(([v, l]) => (
                      <option key={v} value={v}>{l}</option>
                    ))}
                  </select>
                </div>
                <div className="col">
                  <label className="form-label small fw-semibold">Estado</label>
                  <select className="form-select" value={form.status}
                    onChange={e => onChange(f => ({ ...f, status: e.target.value }))}>
                    <option value="active">Activo</option>
                    <option value="inactive">Inactivo</option>
                    <option value="maintenance">Mantenimiento</option>
                  </select>
                </div>
              </div>

              {/* IP Virtual — manual or from subnet map */}
              <div className="mb-3">
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <label className="form-label small fw-semibold mb-0">IP Virtual</label>
                  <div className="btn-group btn-group-sm" role="group">
                    <button type="button"
                      className={`btn btn-sm py-0 ${ipMode === 'manual' ? 'btn-secondary' : 'btn-outline-secondary'}`}
                      onClick={() => setIpMode('manual')}>Manual</button>
                    <button type="button"
                      className={`btn btn-sm py-0 ${ipMode === 'picker' ? 'btn-info text-white' : 'btn-outline-info'}`}
                      onClick={() => setIpMode('picker')}>Del mapa</button>
                  </div>
                </div>

                {ipMode === 'manual' ? (
                  <input className="form-control font-monospace" placeholder="ej. 10.0.1.100"
                    value={form.ip}
                    onChange={e => onChange(f => ({ ...f, ip: e.target.value }))} />
                ) : (
                  <FreeIpPicker companyId={companyId} value={form.ip}
                    onChange={ip => onChange(f => ({ ...f, ip }))} />
                )}
              </div>

              <div className="mb-1">
                <label className="form-label small fw-semibold">Descripción</label>
                <textarea className="form-control" rows={2} placeholder="Propósito del cluster…"
                  value={form.description}
                  onChange={e => onChange(f => ({ ...f, description: e.target.value }))} />
              </div>
            </div>
            <div className="modal-footer py-2">
              <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onClose}>Cancelar</button>
              <button type="submit" className="btn btn-primary btn-sm" disabled={saving}>
                {saving ? 'Guardando…' : editing ? 'Guardar cambios' : 'Crear grupo'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

